/**
 * Upcoming regattas (README "Upcoming regattas"): the /api/landing events that start within the next
 * UPCOMING_DAYS, soonest first, the ones underway now (liveEvents: the landing page's Live now), and the
 * words for them ("Oct 28 – Nov 1", "Starts tomorrow", "Day 2 of 5"). Pure (no DOM), shared by the landing
 * page and the app (the picker, ui/eventCard.ts). RaceSense dates are calendar days, so they are read in UTC. Whether an event has started is the event's own day (eventStarted: its
 * time zone when known, else America/New_York); "Starts tomorrow" counts from the viewer's own day.
 */
import type { LandingEvent } from './racesense/types';

export const DAY = 86_400_000;

/** How far ahead the Upcoming lists look. */
export const UPCOMING_DAYS = 14;

/** Where an event's calendar days are read when its time zone isn't known (most RaceSense events are in the US East). */
export const EVENT_TIME_ZONE = 'America/New_York';

/** A UTC calendar day as a day number. */
export const dayNo = (ms: number): number => Math.floor(ms / DAY);

const dayFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * The calendar day (a day number) at `now` at the event: in `zone`, an IANA time zone or a fixed offset from
 * UTC in ms (a race's timezoneOffset); else, or for a zone Intl doesn't know, in EVENT_TIME_ZONE.
 */
export function eventDayNo(now: number, zone: string | number = EVENT_TIME_ZONE): number {
  if (typeof zone === 'number') return Number.isFinite(zone) ? dayNo(now + zone) : eventDayNo(now);
  let f = dayFormats.get(zone);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric' });
    } catch {
      return zone === EVENT_TIME_ZONE ? dayNo(now) : eventDayNo(now);
    }
    dayFormats.set(zone, f);
  }
  const p: Record<string, number> = {};
  for (const x of f.formatToParts(now)) p[x.type] = Number(x.value);
  return dayNo(Date.UTC(p.year, p.month - 1, p.day));
}

/** Days from the event's own today to its first day (`startMs`, a calendar day in UTC): 1 the day before, 0 or less once it has started. */
export const eventDaysAway = (startMs: number, now: number, zone?: string | number): number => dayNo(startMs) - eventDayNo(now, zone);

/** The event's first day has come where it's sailed (it stays Upcoming all of the day before). */
export const eventStarted = (startMs: number, now: number, zone?: string | number): boolean => eventDaysAway(startMs, now, zone) <= 0;

/** Events that haven't started yet (eventStarted) but start within `days` days, soonest first (title breaks a tie). */
export function upcomingEvents(events: readonly LandingEvent[], now: number, days = UPCOMING_DAYS): LandingEvent[] {
  return events
    .filter((e) => {
      if (!Number.isFinite(e.startMs)) return false;
      const n = eventDaysAway(e.startMs, now);
      return n >= 1 && n <= days;
    })
    .sort((a, b) => a.startMs - b.startMs || a.title.localeCompare(b.title));
}

/** How many Live now cards the landing page shows at most. */
export const LIVE_MAX = 6;

/**
 * Events longer than this many days (first to last, inclusive) stay out of Live now: a season series
 * ("M15 DUN L Winter Series", Oct 16 – Dec 19) sails one day a week, so being inside its dates doesn't
 * mean anyone is racing today. They're still in the app's picker.
 */
export const LIVE_MAX_DAYS = 21;

/** The event's last day as a day number (its first day when the end is missing or before the start). */
const lastDayNo = (e: LandingEvent): number => (Number.isFinite(e.endMs) ? Math.max(dayNo(e.endMs), dayNo(e.startMs)) : dayNo(e.startMs));

/** Which day of the event it is where it's sailed, and how many days it has: { day: 2, days: 5 }. */
export function eventDayOf(e: LandingEvent, now: number, zone?: string | number): { day: number; days: number } {
  return { day: 1 - eventDaysAway(e.startMs, now, zone), days: lastDayNo(e) - dayNo(e.startMs) + 1 };
}

/**
 * Events underway: the event's own today (eventDayNo) is between its first and last day, inclusive, and it
 * runs no longer than LIVE_MAX_DAYS. Most recently started first (title breaks a tie), at most `max`.
 * None of these is in upcomingEvents (that needs a first day still to come).
 */
export function liveEvents(events: readonly LandingEvent[], now: number, max = LIVE_MAX): LandingEvent[] {
  return events
    .filter((e) => {
      if (!Number.isFinite(e.startMs) || e.startMs <= 0) return false;
      const { day, days } = eventDayOf(e, now);
      return days <= LIVE_MAX_DAYS && day >= 1 && day <= days;
    })
    .sort((a, b) => b.startMs - a.startMs || a.title.localeCompare(b.title))
    .slice(0, max);
}

/** "Day 2 of 5", or "Today" for a one-day event. */
export function liveDayText(e: LandingEvent, now: number, zone?: string | number): string {
  const { day, days } = eventDayOf(e, now, zone);
  return days <= 1 ? 'Today' : `Day ${day} of ${days}`;
}

/** The viewer's calendar day at `now` (`tzOffsetMin` as Date#getTimezoneOffset gives it: minutes behind UTC). */
const viewerDay = (now: number, tzOffsetMin = new Date(now).getTimezoneOffset()): number => dayNo(now - tzOffsetMin * 60_000);

/** Whole days from the viewer's today to the event's first day (0 or less: today or before). */
export const daysUntil = (startMs: number, now: number, tzOffsetMin?: number): number => dayNo(startMs) - viewerDay(now, tzOffsetMin);

/** "Starts today", "Starts tomorrow", "Starts in 5 days". */
export function startsInText(startMs: number, now: number, tzOffsetMin?: number): string {
  const n = daysUntil(startMs, now, tzOffsetMin);
  if (n <= 0) return 'Starts today';
  if (n === 1) return 'Starts tomorrow';
  return `Starts in ${n} days`;
}

const fmt = (ms: number, o: Intl.DateTimeFormatOptions): string => new Date(ms).toLocaleDateString('en-US', { timeZone: 'UTC', ...o });

/** "Oct 28 – Nov 1", "Oct 28 – 30", or "Oct 28" for a one-day event ('' with no start). */
export function dateRangeText(startMs: number, endMs?: number): string {
  if (!Number.isFinite(startMs) || startMs <= 0) return '';
  const from = fmt(startMs, { month: 'short', day: 'numeric' });
  if (endMs == null || !Number.isFinite(endMs) || dayNo(endMs) <= dayNo(startMs)) return from;
  const sameMonth = fmt(startMs, { month: 'short' }) === fmt(endMs, { month: 'short' });
  return `${from} – ${sameMonth ? fmt(endMs, { day: 'numeric' }) : fmt(endMs, { month: 'short', day: 'numeric' })}`;
}

/** "Wed, Oct 28". */
export const dayText = (ms: number): string => fmt(ms, { weekday: 'short', month: 'short', day: 'numeric' });
